import React, { useMemo } from "react";
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from "antd";

import { formatMetricHeader } from "../../services/marketReferencePriceViewModel";
import { EMPTY_VALUE } from "../../utils/formatters";

const { Text } = Typography;

const MarketSeriesPanel = ({
    filters,
    onFilterChange,
    onSearch,
    onReset,
    sourceOptions,
    sourcesLoading,
    series,
    seriesLoading,
    pagination,
    selectedSeriesId,
    onSelectSeries,
    onTableChange,
}) => {
    const columns = useMemo(() => [
        {
            title: "曲线",
            key: "series_name",
            width: 240,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{record.series_name}</Text>
                    <Text type="secondary">{record.series_code}</Text>
                    {record.sheet_key ? <Text type="secondary">Sheet: {record.sheet_key}</Text> : null}
                </Space>
            ),
        },
        {
            title: "来源 / 市场",
            key: "source_market",
            width: 180,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{record.source?.source_name || EMPTY_VALUE}</Text>
                    <Text type="secondary">{record.market_region || EMPTY_VALUE}</Text>
                </Space>
            ),
        },
        {
            title: "报价口径",
            key: "quote_basis",
            width: 150,
            render: (_, record) => (
                <Space direction="vertical" size={0}>
                    <Text>{`${record.quote_currency || EMPTY_VALUE}/${record.quote_unit || EMPTY_VALUE}`}</Text>
                    <Text type="secondary">{record.delivery_basis || EMPTY_VALUE}</Text>
                </Space>
            ),
        },
        {
            title: "字段集合",
            dataIndex: "field_schema",
            key: "field_schema",
            render: (fieldSchema = []) => {
                if (!fieldSchema.length) {
                    return EMPTY_VALUE;
                }

                const visibleFields = fieldSchema.slice(0, 3);
                const extraCount = fieldSchema.length - visibleFields.length;

                return (
                    <Space size={[4, 4]} wrap>
                        {visibleFields.map((field) => (
                            <Tag key={field.key} color="geekblue">
                                {formatMetricHeader(field)}
                            </Tag>
                        ))}
                        {extraCount > 0 ? <Tag>+{extraCount}</Tag> : null}
                    </Space>
                );
            },
        },
    ], []);

    return (
        <Card title="Market Series" size="small" style={{ height: "100%" }}>
            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                <Space wrap style={{ width: "100%" }}>
                    <Select
                        allowClear
                        showSearch
                        placeholder="来源"
                        style={{ width: 220 }}
                        options={sourceOptions}
                        loading={sourcesLoading}
                        value={filters.source_id}
                        onChange={(value) => onFilterChange((current) => ({ ...current, source_id: value }))}
                    />
                    <Input
                        placeholder="市场区域，如 SINGAPORE"
                        style={{ width: 220 }}
                        value={filters.market_region || ""}
                        onChange={(event) => onFilterChange((current) => ({ ...current, market_region: event.target.value || undefined }))}
                    />
                    <Input
                        placeholder="搜索 code / name / sheet"
                        style={{ width: 240 }}
                        value={filters.search_term || ""}
                        onChange={(event) => onFilterChange((current) => ({ ...current, search_term: event.target.value || undefined }))}
                        onPressEnter={onSearch}
                    />
                    <Button type="primary" onClick={onSearch}>
                        查询
                    </Button>
                    <Button onClick={onReset}>重置</Button>
                </Space>

                <Table
                    rowKey="id"
                    size="small"
                    loading={seriesLoading}
                    columns={columns}
                    dataSource={series}
                    pagination={pagination}
                    rowSelection={{
                        type: "radio",
                        selectedRowKeys: selectedSeriesId ? [selectedSeriesId] : [],
                        onChange: (selectedRowKeys) => onSelectSeries(selectedRowKeys[0]),
                    }}
                    onChange={onTableChange}
                    scroll={{ x: 760 }}
                    onRow={(record) => ({
                        onClick: () => onSelectSeries(record.id),
                    })}
                    locale={{ emptyText: "暂无可浏览的市场曲线" }}
                />
            </Space>
        </Card>
    );
};

export default MarketSeriesPanel;
